import {
  getTaxDecimal,
  isDtoolsSiQuoteLocked,
  isQuoteDraftEditable,
  QUERY_KEYS,
  updateRecord,
  useQuoteDetailsQuery,
  useQuoteQuery,
} from '@/lib/api'
import { isQueryLoaded } from '@/lib/plugins/plugin-readiness'
import { usePluginMutation } from '@/lib/plugins/use-plugin-mutation'
import { usePluginQueryKeysToInvalidateStore } from '@/lib/store'
import { Decimal } from 'decimal.js'
import { memo, useCallback, useEffect } from 'react'

export const TaxRecalculationPlugin = memo(function TaxRecalculationPlugin() {
  const { data: quote, dataUpdatedAt: quoteUpdatedAt } = useQuoteQuery()
  const { data: quoteDetails, dataUpdatedAt: quoteDetailsUpdatedAt } = useQuoteDetailsQuery()

  const addQueryKeys = usePluginQueryKeysToInvalidateStore((state) => state.addQueryKeys)

  const isDraft = isQuoteDraftEditable(quote)
  const isDtoolsSiLocked = isDtoolsSiQuoteLocked(quote)

  const computeUpdates = useCallback(() => {
    if (!quoteDetails || !quote?.dsy_taxcode) return []
    return quoteDetails
      .map((detail) => {
        const isTaxable = !!detail.msdyn_taxable
        const quantityDecimal = new Decimal(detail.quantity)
        const unitPriceDecimal = new Decimal(detail.priceperunit ?? 0)
        const currentTaxDecimal = new Decimal(detail.tax ?? 0)
        const newTaxDecimal = isTaxable ? getTaxDecimal(unitPriceDecimal, quantityDecimal) : new Decimal(0)
        if (newTaxDecimal.eq(currentTaxDecimal)) return null
        const totalPriceWithTaxDecimal = unitPriceDecimal.mul(quantityDecimal).plus(newTaxDecimal).toDecimalPlaces(2)
        return {
          quoteDetail: detail,
          tax: newTaxDecimal.toNumber(),
          extendedamount: totalPriceWithTaxDecimal.toNumber(),
        }
      })
      .filter((update): update is NonNullable<typeof update> => update !== null)
  }, [quoteDetails, quote?.dsy_taxcode])

  const {
    isPending: isRecalculatingTax,
    schedule,
    cancel,
  } = usePluginMutation({
    flushId: 'tax-recalculation',
    fingerprint: () => [quoteDetailsUpdatedAt, quoteUpdatedAt],
    scopeKey: quote?.quoteid,
    mutationKey: ['plugin', 'recalculate-tax'],
    isReady: () => isQueryLoaded(quoteDetailsUpdatedAt),
    notReadyMessage: 'Quote details are still loading. Please try activating again in a moment.',
    isDirty: () => !!isDraft && !isDtoolsSiLocked && computeUpdates().length > 0,
    mutationFn: async () => {
      const updates = computeUpdates()
      await Promise.all(
        updates.map((update) =>
          updateRecord<{ tax: number; extendedamount: number }>('quotedetails', update.quoteDetail.quotedetailid, {
            tax: update.tax,
            extendedamount: update.extendedamount,
          })
        )
      )
      return updates
    },
    onSuccess: async (data) => {
      const queryKeys = [QUERY_KEYS.quoteDetails()]
      const uniqueRooms = new Set<string>()
      const uniqueSystems = new Set<number>()

      for (const update of data) {
        if (update.quoteDetail.dsy_cpqfields._dsy_room_value) {
          uniqueRooms.add(update.quoteDetail.dsy_cpqfields._dsy_room_value)
        }
        if (update.quoteDetail.dsy_cpqfields.dsy_system) {
          uniqueSystems.add(update.quoteDetail.dsy_cpqfields.dsy_system)
        }
      }

      for (const roomId of uniqueRooms) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ roomId }))
        for (const systemId of uniqueSystems) {
          queryKeys.push(QUERY_KEYS.quoteDetails({ roomId, systemId }))
        }
      }
      for (const systemId of uniqueSystems) {
        queryKeys.push(QUERY_KEYS.quoteDetails({ systemId }))
      }
      addQueryKeys(queryKeys)
    },
    onError: (error) => {
      console.error('TaxRecalculationPlugin: ~ error:', error)
    },
  })

  useEffect(() => {
    if (
      isRecalculatingTax ||
      !quoteDetails ||
      !isQueryLoaded(quoteDetailsUpdatedAt) ||
      !quote?.dsy_taxcode ||
      !isDraft ||
      isDtoolsSiLocked
    )
      return
    schedule(1000)
    return cancel
  }, [
    quoteDetails,
    quoteDetailsUpdatedAt,
    quote?.dsy_taxcode,
    isRecalculatingTax,
    isDraft,
    isDtoolsSiLocked,
    schedule,
    cancel,
  ])

  return null
})
